import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { currencyCode, localeNumber } from '../../utils/region';

interface ServiceCompany {
  id: string;
  name: string;
  short_name: string;
  service_count: number;
}

interface Service {
  id: string;
  company_id: string;
  name: string;
  code?: string;
  description: string;
  price: number;
  currency: string;
  unit: string;
  notes: string;
  sort_order: number;
  active: number;
  resource_user_id?: string;
  resource_name?: string;
  client_count?: number;
  pass_through?: number;
  no_wht?: number;   // never takes the 3% a company client's lines get by default (Bugify #23e8e744)
  has_flow?: number;
  monthly_accounting?: number;   // owner Q7: invoice email defaults to the monthly fee request
  desk?: string; family?: string; desk_label?: string; family_label?: string;   // desk consoles (2026-09-23)
  created_by_name: string;
  created_at: number;
  updated_by_name?: string;
  updated_at?: number;
}

const CURRENCIES = ['THB', 'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'SGD', 'AUD'];

const COMPANY_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  default:    { bg: '#F0F9FF', border: '#BAE6FD', text: '#0369A1', dot: '#0EA5E9' },
  second:     { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D', dot: '#22C55E' },
};

function companyColor(idx: number) {
  return idx === 0 ? COMPANY_COLORS.default : COMPANY_COLORS.second;
}

// ── Modal ─────────────────────────────────────────────────────────────────
interface ModalProps {
  companies: ServiceCompany[];
  initial?: Partial<Service> & { company_id?: string };
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}

function ServiceModal({ companies, initial = {}, onSave, onClose }: ModalProps) {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState(initial.company_id || companies[0]?.id || '');
  const [name, setName] = useState(initial.name || '');
  const [code, setCode] = useState(initial.code || '');
  const [description, setDescription] = useState(initial.description || '');
  const [price, setPrice] = useState(String(initial.price ?? ''));
  const [currency, setCurrency] = useState(initial.currency || currencyCode());
  const [unit, setUnit] = useState(initial.unit || '');
  const [notes, setNotes] = useState(initial.notes || '');
  const [active, setActive] = useState(initial.active !== undefined ? !!initial.active : true);
  const [monthlyAccounting, setMonthlyAccounting] = useState(!!Number(initial.monthly_accounting || 0));
  const [resourceUserId, setResourceUserId] = useState((initial as any).resource_user_id || '');
  // Desk filing (2026-09-23): which console (SLF™ Visa / Company / License / Legal Matters) lists this
  // service and under which family chip. The vocabulary comes from the server's one table.
  const [desk, setDesk] = useState(((initial as any).desk || '') === 'none' ? '' : ((initial as any).desk || ''));   // 'none' = explicitly un-filed
  const [family, setFamily] = useState((initial as any).family || '');
  const [desks, setDesks] = useState<{ key: string; label: string; families: { key: string; label: string }[] }[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { api.getServiceUsers().then(setUsers).catch(() => {}); api.serviceDesks().then((d: any) => setDesks(d.desks || [])).catch(() => {}); }, []);

  const handle = async () => {
    if (!name.trim()) return setError('Service name is required');
    setSaving(true); setError('');
    try {
      await onSave({ company_id: companyId, name: name.trim(), code: code.trim(), description, price: parseFloat(price) || 0, currency, unit, notes, active: active ? 1 : 0, resource_user_id: resourceUserId, desk, family: desk ? family : '', monthly_accounting: monthlyAccounting ? 1 : 0 });
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 520, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)', padding: 28, display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
            {initial.id ? t('sv_edit') : t('sv_add')}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}>
            <i className="ti ti-x" aria-hidden />
          </button>
        </div>

        {/* Company — editable on BOTH create and edit, so a service filed under the wrong company
            (Legal vs Accounting) can be corrected. */}
        <div>
          <label style={labelStyle}>Company</label>
          <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={inputStyle}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Name + Code */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>{t('sv_name')} *</label>
            <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder={t('sv_name')} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t('sv_code')}</label>
            <input value={code} onChange={e => setCode(e.target.value)} style={inputStyle} placeholder={t('sv_code')} />
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>{t('sv_description')}</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder={t('sv_description')} />
        </div>

        {/* Price + Currency */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>{t('sv_price')}</label>
            <input value={price} onChange={e => setPrice(e.target.value)} style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t('sv_currency')}</label>
            <select value={currency} onChange={e => setCurrency(e.target.value)} style={inputStyle}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Unit */}
        <div>
          <label style={labelStyle}>{t('sv_unit')}</label>
          <input value={unit} onChange={e => setUnit(e.target.value)} style={inputStyle} placeholder="e.g. per month, per case, per hour" />
        </div>

        {/* Notes */}
        <div>
          <label style={labelStyle}>{t('sv_notes')}</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder={t('sv_notes')} />
        </div>

        {/* Responsible person (becomes the Resource on jobs from this service) */}
        <div>
          <label style={labelStyle}>{t('sv_resource')}</label>
          <select value={resourceUserId} onChange={e => setResourceUserId(e.target.value)} style={inputStyle}>
            <option value="">{t('sv_resource_default')}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>

        {/* Desk + family (2026-09-23) */}
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t('sv_desk')}</label>
            <select value={desk} onChange={e => { setDesk(e.target.value); setFamily(''); }} style={inputStyle}>
              <option value="">{t('sv_desk_none')}</option>
              {desks.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t('sv_family')}</label>
            <select value={family} onChange={e => setFamily(e.target.value)} style={inputStyle} disabled={!desk}>
              <option value="">—</option>
              {(desks.find(d => d.key === desk)?.families || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
        </div>

        {/* Monthly accounting service (owner Q7, 2026-10-03): an invoice carrying this service opens its
            email on the monthly fee request instead of the payment request (switchable there). */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={monthlyAccounting} onChange={e => setMonthlyAccounting(e.target.checked)} style={{ width: 15, height: 15, marginTop: 1 }} />
          <span>
            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{t('mf_service_flag')}</span>
            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('mf_service_flag_hint')}</span>
          </span>
        </label>

        {/* Active toggle */}
        {initial.id && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} style={{ width: 15, height: 15 }} />
            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{t('sv_active')}</span>
          </label>
        )}

        {error && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <button onClick={onClose} style={btnSecondaryStyle}>Cancel</button>
          <button onClick={handle} disabled={saving} style={{ ...btnPrimaryStyle, opacity: saving ? 0.7 : 1 }}>
            {saving ? '…' : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Services({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Each service can carry its own client-facing workflow; the editor lives in the Workflows module,
  // so the card links straight into it. Only shown to staff who actually hold that module.
  const me = useStore(st => st.me);
  const canWorkflow = !!me && (me.role === 'admin' || (me.modules || []).includes('workflows'));
  const [customFlows, setCustomFlows] = useState<Set<string>>(new Set());
  useEffect(() => { if (canWorkflow) api.wfFlowCustomized().then((d: any) => setCustomFlows(new Set(d.serviceIds || []))).catch(() => {}); }, [canWorkflow]);
  const [companies, setCompanies] = useState<ServiceCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [services, setServices] = useState<Service[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<null | { mode: 'add'; companyId: string } | { mode: 'edit'; service: Service }>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [clientsFor, setClientsFor] = useState<Service | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);   // PDF generation takes a few seconds — show a spinner

  const loadCompanies = useCallback(async () => {
    try {
      const data = await api.getServiceCompanies();
      setCompanies(data);
      if (!selectedCompanyId && data.length > 0) setSelectedCompanyId(data[0].id);
    } catch {}
  }, [selectedCompanyId]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getServices(selectedCompanyId || undefined);
      setServices(data);
    } catch {} finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => { loadCompanies(); }, []); // eslint-disable-line
  useEffect(() => { if (selectedCompanyId !== undefined) loadServices(); }, [selectedCompanyId]); // eslint-disable-line

  const handleSave = async (data: any) => {
    if (modal?.mode === 'edit') {
      await api.updateService(modal.service.id, data);
    } else {
      await api.createService(data);
    }
    await loadCompanies();
    await loadServices();
  };

  const handleDelete = async (id: string) => {
    await api.deleteService(id);
    setDeleteId(null);
    await loadCompanies();
    await loadServices();
  };

  const filtered = services.filter(s => {
    if (!q.trim()) return true;
    const lq = q.toLowerCase();
    return s.name.toLowerCase().includes(lq) || s.description.toLowerCase().includes(lq) || s.notes.toLowerCase().includes(lq);
  });

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);
  // The pass-through (代收代付) flag is an Accounting-only concept for now.
  const isAccounting = /account/i.test(selectedCompany?.name || '') || /account/i.test(selectedCompany?.short_name || '');
  const togglePassThrough = async (s: Service) => {
    await api.updateService(s.id, { pass_through: s.pass_through ? 0 : 1 });
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, pass_through: s.pass_through ? 0 : 1 } : x));
  };
  // "No WHT" (Calem, Bugify #23e8e744): a company client's quotation and invoice lines take 3% withholding tax by
  // default; a service ticked here never does (tax payments, Social Security, postage, the auditor's fee). Unlike
  // Collect & remit it is on every company's services, as any of them may quote a company client.
  const toggleNoWht = async (s: Service) => {
    const next = s.no_wht ? 0 : 1;
    await api.updateService(s.id, { no_wht: next });
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, no_wht: next } : x));
  };
  // Whether this service is RUN as a workflow (owner 2026-09-25). Every service had one, and a
  // one-off document, a company stamp, a page of notarisation or a postage charge has no sequence
  // for a client to watch — the matter just sits at 0/0. Off means the + wizard stops offering it
  // and the server refuses a matter on it; the service itself, its price and its invoicing are
  // untouched. On is the default, so nothing changes until this is switched.
  const toggleHasFlow = async (s: Service) => {
    const next = (s.has_flow ?? 1) ? 0 : 1;
    await api.updateService(s.id, { has_flow: next });
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, has_flow: next } : x));
  };

  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {/* Header — when embedded (Peak six-pack) keep the action buttons but drop the icon/title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', minHeight: 52 }}>
        <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        {!embedded && <i className="ti ti-packages" style={{ fontSize: 20, color: 'var(--accent)' }} aria-hidden />}
        {!embedded && <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_services')}</div>}
        {/* Whose price list is on screen. It used to have a strip of its own between this bar and
            the list, together with the search box; both moved up here (owner 2026-09-25) and that
            strip is gone, which gives the list back a full row on every screen. */}
        {selectedCompany && (
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selectedCompany.name}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {selectedCompanyId && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)', pointerEvents: 'none' }} aria-hidden />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={t('sv_search')}
              style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7, fontSize: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none', width: 220 }}
            />
          </div>
        )}
        {selectedCompanyId && (
          <button
            onClick={async () => {
              if (pdfBusy) return;
              setPdfBusy(true);
              try { await api.exportServicesPdf(selectedCompanyId, `${(selectedCompany?.name || 'services').replace(/[^\w-]+/g, '_')}_services.pdf`); }
              catch (e: any) { alert(e.message); }
              finally { setPdfBusy(false); }
            }}
            disabled={pdfBusy}
            style={{ ...btnSecondaryStyle, opacity: pdfBusy ? 0.65 : 1, cursor: pdfBusy ? 'wait' : 'pointer' }}
            title={t('sv_download_pdf')}
          >
            <i className={pdfBusy ? 'ti ti-loader-2' : 'ti ti-download'} style={{ fontSize: 14, marginRight: 5, animation: pdfBusy ? 'spin 0.8s linear infinite' : undefined }} aria-hidden />
            {pdfBusy ? t('sv_generating_pdf') : t('sv_download_pdf')}
          </button>
        )}
        {selectedCompanyId && (
          <button
            onClick={() => setModal({ mode: 'add', companyId: selectedCompanyId })}
            style={btnPrimaryStyle}
          >
            <i className="ti ti-plus" style={{ fontSize: 14, marginRight: 5 }} aria-hidden />
            {t('sv_add')}
          </button>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* ── Left sidebar: company list ──────────────────────────────── */}
        <aside style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--border)', background: 'var(--bg-surface)', overflowY: 'auto', padding: '12px 0' }}>
          {companies.map((c, idx) => {
            const col = companyColor(idx);
            const isActive = c.id === selectedCompanyId;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedCompanyId(c.id)}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 16px',
                  background: isActive ? 'var(--bg-active)' : 'transparent',
                  border: 'none', borderLeft: `3px solid ${isActive ? col.dot : 'transparent'}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.dot, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? 'var(--accent)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.short_name || c.name}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1 }}>
                    {c.service_count} services
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* ── Right main area ──────────────────────────────────────────── */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Service list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60, color: 'var(--text-tertiary)' }}>
                <i className="ti ti-loader" style={{ fontSize: 24, animation: 'spin 1s linear infinite' }} aria-hidden />
                <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 80, gap: 10, color: 'var(--text-tertiary)' }}>
                <i className="ti ti-packages" style={{ fontSize: 36 }} aria-hidden />
                <div style={{ fontSize: 13 }}>{t('sv_empty')}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.map(s => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    onEdit={() => setModal({ mode: 'edit', service: s })}
                    onDelete={() => setDeleteId(s.id)}
                    onShowClients={() => setClientsFor(s)}
                    showPassThrough={isAccounting}
                    onTogglePassThrough={() => togglePassThrough(s)}
                    onToggleNoWht={() => toggleNoWht(s)}
                    onToggleHasFlow={() => toggleHasFlow(s)}
                    onWorkflow={canWorkflow ? () => navigate(`/m/workflows?v=flows&flow=${s.id}`) : undefined}
                    hasFlow={customFlows.has(s.id)}
                    onPreview={canWorkflow ? () => navigate(`/m/workflows?preview=${s.id}`) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal */}
      {modal && (
        <ServiceModal
          companies={companies}
          initial={modal.mode === 'edit' ? { ...modal.service, company_id: modal.service.company_id } : { company_id: modal.companyId }}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Companies using this service */}
      {clientsFor && <ServiceClientsModal service={clientsFor} onClose={() => setClientsFor(null)} />}

      {/* Delete confirm */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: '24px 28px', width: 340, boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('sv_confirm_delete')}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setDeleteId(null)} style={btnSecondaryStyle}>Cancel</button>
              <button onClick={() => handleDelete(deleteId)} style={{ ...btnPrimaryStyle, background: '#E24B4A' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Service card ───────────────────────────────────────────────────────────
function ServiceCard({ service: s, onEdit, onDelete, onShowClients, showPassThrough, onTogglePassThrough, onToggleNoWht, onToggleHasFlow, onWorkflow, hasFlow, onPreview }: { service: Service; onEdit: () => void; onDelete: () => void; onShowClients: () => void; showPassThrough?: boolean; onTogglePassThrough?: () => void; onToggleNoWht?: () => void; onToggleHasFlow?: () => void; onWorkflow?: () => void; hasFlow?: boolean; onPreview?: () => void }) {
  const { t } = useTranslation();
  const hasPrice = s.price > 0;
  const count = s.client_count || 0;
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '14px 16px',
      display: 'flex', alignItems: 'flex-start', gap: 14,
      opacity: s.active ? 1 : 0.55,
    }}>
      {/* Icon */}
      <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'var(--bg-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--accent)' }}>
        <i className="ti ti-star" style={{ fontSize: 18 }} aria-hidden />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{s.name}</span>
          {s.code && (
            <span title="Job Code" style={{ fontSize: 10.5, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'var(--accent)', background: 'var(--bg-active)', borderRadius: 5, padding: '1px 6px' }}>{s.code}</span>
          )}
          {!s.active && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, background: 'var(--c-gray-100)', color: 'var(--text-tertiary)' }}>Inactive</span>
          )}
          <button
            onClick={e => { e.stopPropagation(); if (count > 0) onShowClients(); }}
            disabled={count === 0}
            title={count > 0 ? 'View the companies using this service' : 'No companies use this service yet'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 8, border: '1px solid', borderColor: count > 0 ? 'var(--accent)' : 'var(--border)', background: count > 0 ? 'var(--bg-active)' : 'transparent', color: count > 0 ? 'var(--accent)' : 'var(--text-tertiary)', fontSize: 11, fontWeight: 700, cursor: count > 0 ? 'pointer' : 'default' }}>
            <i className="ti ti-building" style={{ fontSize: 12 }} aria-hidden />
            {count} {count === 1 ? 'company' : 'companies'}
          </button>
        </div>
        {s.description && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{s.description}</div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
          {showPassThrough && (
            <label
              onClick={e => e.stopPropagation()}
              title="Collect & remit: the client pays the firm and the firm pays a third party (e.g. a government office) on the client's behalf. This fee is NOT the firm's income, so it is excluded from Receipts."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: s.pass_through ? 700 : 500, color: s.pass_through ? '#B45309' : 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!s.pass_through} onChange={onTogglePassThrough} style={{ cursor: 'pointer' }} />
              {/* The product UI is English/Thai — the Chinese term belongs in the code comments,
                  not on a screen Calem and Nick use (owner 2026-09-17). */}
              Collect &amp; remit (excl. receipts)
            </label>
          )}
          {onToggleNoWht && (
            <label onClick={e => e.stopPropagation()} title={t('sv_no_wht_hint')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: s.no_wht ? 700 : 500, color: s.no_wht ? '#B45309' : 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!s.no_wht} onChange={onToggleNoWht} style={{ cursor: 'pointer' }} />
              {t('sv_no_wht')}
            </label>
          )}
          {/* Only a desk service can be run as a workflow, so only those carry the switch. */}
          {!!s.desk_label && (
            <label onClick={e => e.stopPropagation()}
              title="Run this service as a workflow: a sequence of steps the client can follow on their own page. Turn it off for one-off items — a document, a stamp, a postage charge — which have no steps to watch. Price and invoicing are unaffected."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: (s.has_flow ?? 1) ? 500 : 700, color: (s.has_flow ?? 1) ? 'var(--text-secondary)' : '#B45309', cursor: 'pointer' }}>
              <input type="checkbox" checked={(s.has_flow ?? 1) === 1} onChange={onToggleHasFlow} style={{ cursor: 'pointer' }} />
              {t('sv_has_flow')}
            </label>
          )}
          {!!Number(s.monthly_accounting || 0) && (
            <span title={t('mf_service_flag_hint')} style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 999, background: '#ECFDF5', color: '#0F766E', whiteSpace: 'nowrap' }}>
              <i className="ti ti-calendar-repeat" style={{ fontSize: 11 }} aria-hidden /> {t('mf_service_flag')}
            </span>
          )}
          {s.desk_label && (
            <span title={t('sv_desk')} style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 999, background: 'var(--bg-active)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              <i className="ti ti-layout-grid" style={{ fontSize: 11 }} aria-hidden /> {s.desk_label}{s.family_label ? ` · ${s.family_label}` : ''}
            </span>
          )}
          {hasPrice && (
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
              {s.currency} {s.price.toLocaleString(localeNumber(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              {s.unit && <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 4 }}>/ {s.unit}</span>}
            </span>
          )}
          {!hasPrice && s.unit && (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.unit}</span>
          )}
          {s.resource_name && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Responsible person">
              <i className="ti ti-user" style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
              {s.resource_name}
            </span>
          )}
          {s.notes && (
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{s.notes}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {onWorkflow && (
          <button onClick={onWorkflow} style={{ ...iconBtnStyle, color: hasFlow ? '#0E7C66' : undefined }} title={hasFlow ? t('sv_workflow_custom') : t('sv_workflow')}>
            <i className={hasFlow ? 'ti ti-route' : 'ti ti-route-off'} style={{ fontSize: 14 }} aria-hidden />
          </button>
        )}
        {onPreview && (
          <button onClick={onPreview} style={iconBtnStyle} title={t('sv_preview')}>
            <i className="ti ti-eye" style={{ fontSize: 14 }} aria-hidden />
          </button>
        )}
        <button onClick={onEdit} style={iconBtnStyle} title="Edit">
          <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden />
        </button>
        <button onClick={onDelete} style={{ ...iconBtnStyle, color: '#E24B4A' }} title="Delete">
          <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />
        </button>
      </div>
    </div>
  );
}

// ── Companies-using-this-service modal ──────────────────────────────────────
function ServiceClientsModal({ service, onClose }: { service: Service; onClose: () => void }) {
  const [rows, setRows] = useState<{ id: string; name: string; taxId: string; isIndividual: boolean; quantity: number; unitPrice: number }[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    api.getServiceClients(service.id).then((d: any) => { if (on) setRows(d || []); }).catch(() => { if (on) setRows([]); }).finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [service.id]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 540, maxWidth: '92vw', maxHeight: '85vh', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-building" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{service.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{loading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'company uses' : 'companies use'} this service`}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }} aria-label="Close"><i className="ti ti-x" aria-hidden /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22, animation: 'spin 1s linear infinite' }} aria-hidden /></div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)', fontSize: 13 }}>No companies use this service yet.</div>
          ) : rows.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 'var(--radius-md)', background: i % 2 ? 'transparent' : 'var(--bg-app)' }}>
              <div style={{ width: 24, textAlign: 'right', fontSize: 12, color: 'var(--text-tertiary)' }}>{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name}
                  {c.isIndividual && <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--bg-active)', borderRadius: 5, padding: '1px 6px' }}>Individual</span>}
                </div>
                {c.taxId && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'ui-monospace, monospace' }}>{c.taxId}</div>}
              </div>
              {c.unitPrice > 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{service.currency} {Number(c.unitPrice).toLocaleString(localeNumber(), { maximumFractionDigits: 2 })}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)',
  background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
  boxSizing: 'border-box',
};
const btnPrimaryStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '8px 16px',
  background: 'var(--accent)', color: '#fff', border: 'none',
  borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const btnSecondaryStyle: React.CSSProperties = {
  padding: '8px 14px', background: 'var(--bg-app)',
  color: 'var(--text-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
};
const iconBtnStyle: React.CSSProperties = {
  width: 30, height: 30, border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)', background: 'var(--bg-app)',
  color: 'var(--text-tertiary)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
